import { useState, useEffect } from 'react';
import { usePDIContext } from '../context/PDIContext';
import { decodeVIN, getOldStockWarning, formatVIN } from '../utils/vinDecoder';
import {
  Hash,
  AlertTriangle,
  CheckCircle2,
  Factory,
  Calendar,
  MapPin,
  AlertOctagon,
  ChevronDown,
  ChevronUp,
  Info,
  Clock,
} from 'lucide-react';

const VINDecoder = () => {
  const { carProfile, updateCarProfile } = usePDIContext();
  const [vin, setVin] = useState(carProfile?.vin || '');
  const [decodedData, setDecodedData] = useState(null);
  const [oldStockWarning, setOldStockWarning] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (vin.length >= 17) {
      const result = decodeVIN(vin);
      if (result.success) {
        setDecodedData(result);
        setOldStockWarning(getOldStockWarning(result));
        setError(null);
      } else {
        setDecodedData(null);
        setOldStockWarning(null);
        setError(result.error);
      }
    } else {
      setDecodedData(null);
      setOldStockWarning(null);
      setError(null);
    }
  }, [vin]);

  const handleVINChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
    setVin(value);
    
    // Auto-save to profile
    if (value.length === 17) {
      updateCarProfile({ ...carProfile, vin: value });
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="flex items-center gap-2">
          <Hash className="w-5 h-5 text-brand-400" />
          <h3 className="font-semibold">VIN Decoder</h3>
        </div>
        <p className="text-xs text-slate-300 mt-1">
          Decode your Vehicle Identification Number
        </p>
      </div>

      {/* Input */}
      <div className="p-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Enter VIN (17 characters)
        </label>
        <div className="relative">
          <input
            type="text"
            value={vin}
            onChange={handleVINChange}
            maxLength={17}
            placeholder="e.g., MA3FJEB1S00123456"
            className="font-mono text-lg tracking-wider uppercase"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
            {vin.length}/17
          </span>
        </div>

        {/* Validation Error */}
        {error && (
          <div className="mt-3 p-3 bg-danger-50 border border-danger-200 rounded-xl flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-danger-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-danger-700">{error}</p>
          </div>
        )}

        {/* Decoded Result */}
        {decodedData && (
          <div className="mt-4 space-y-3 animate-fade-in">
            {/* Success Badge */}
            <div className="flex items-center gap-2 text-brand-600">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-sm font-medium">VIN Decoded Successfully</span>
            </div>

            {/* Old Stock Warning */}
            {oldStockWarning?.isOldStock && (
              <div className="p-4 bg-danger-50 border-2 border-danger-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertOctagon className="w-6 h-6 text-danger-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-danger-800">
                      ⚠️ Old Stock Vehicle Detected
                    </p>
                    <p className="text-sm text-danger-700 mt-1">
                      {oldStockWarning.message}
                    </p>
                    <ul className="mt-3 space-y-1">
                      {oldStockWarning.details.map((detail, i) => (
                        <li key={i} className="text-sm text-danger-700 flex items-start gap-2">
                          <span className="text-danger-400">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Potentially Old Warning */}
            {oldStockWarning?.potentiallyOld && (
              <div className="p-4 bg-accent-50 border border-accent-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-accent-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-accent-800">
                      Check Manufacturing Date
                    </p>
                    <p className="text-sm text-accent-700 mt-1">
                      {oldStockWarning.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Key Info Cards */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Factory className="w-4 h-4" />
                  <span className="text-xs">Manufacturer</span>
                </div>
                <p className="font-semibold text-slate-800">{decodedData.manufacturer}</p>
                <p className="text-xs text-slate-500">{decodedData.country}</p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs">Model Year</span>
                </div>
                <p className="font-semibold text-slate-800">
                  {decodedData.modelYear || 'Unknown'}
                </p>
                <p className="text-xs text-slate-500">Code: {decodedData.yearCode}</p>
              </div>
            </div>

            {decodedData.plantLocation && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <MapPin className="w-4 h-4" />
                  <span className="text-xs">Manufacturing Plant</span>
                </div>
                <p className="font-semibold text-slate-800">{decodedData.plantLocation}</p>
              </div>
            )}

            {/* Show More Details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-slate-600 hover:text-slate-800"
            >
              {showDetails ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide Details
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show More Details
                </>
              )}
            </button>

            {showDetails && (
              <div className="p-4 bg-slate-50 rounded-xl space-y-2 animate-slide-down">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">WMI Code</span>
                  <span className="font-mono text-slate-800">{decodedData.wmi}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Plant Code</span>
                  <span className="font-mono text-slate-800">{decodedData.plantCode}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Serial Number</span>
                  <span className="font-mono text-slate-800">{decodedData.serialNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Formatted VIN</span>
                  <span className="font-mono text-slate-800">{formatVIN(vin)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Help Text */}
        {!decodedData && !error && vin.length < 17 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Where to find VIN:</p>
                <ul className="mt-1 space-y-1 text-blue-600">
                  <li>• Dashboard (visible through windshield)</li>
                  <li>• Door jamb sticker (driver's side)</li>
                  <li>• Vehicle documents (invoice, RC)</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VINDecoder;
